import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Keyboard, ArrowCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { displayCombo, IS_MAC } from "@/lib/keyboardDisplay";
import InlineShortcutAssigner from "@/components/InlineShortcutAssigner";
import { useShortcuts } from "@/context/ShortcutsContext";

// v1.28.76 — Super User page to configure keyboard shortcuts globally.
// The row-level ✎ / + icon opens the same visual keyboard grid used
// throughout the app (see `InlineShortcutAssigner`) so every combo
// picker in the app behaves identically.
const BLOCKED = ["Ctrl+C", "Ctrl+V", "Ctrl+X", "Ctrl+A", "Ctrl+Z", "Ctrl+Y", "Ctrl+T", "Ctrl+W", "Ctrl+N", "Ctrl+S", "Ctrl+F"];

export default function KeyboardShortcuts() {
    const { shortcuts, refresh } = useShortcuts();
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState("");

    // Always show a fresh copy on mount so a save propagated from another
    // tab is picked up right away.
    useEffect(() => { refresh(); }, [refresh]);

    const resetDefaults = async () => {
        if (!window.confirm("Reset all shortcuts to defaults?")) return;
        setBusy(true);
        try {
            await api.put("/shortcuts/reset");
            toast.success("Shortcuts reset to defaults — applies to every user immediately");
            window.dispatchEvent(new CustomEvent("micm-shortcuts-updated"));
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    // Group by category (Navigation / Search / Action / Menu / ...) so the
    // page reads like a real shortcut cheat-sheet.
    const grouped = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = (shortcuts || []).filter((s) =>
            !q || s.label.toLowerCase().includes(q) || (s.combo || "").toLowerCase().includes(q)
        );
        const map = new Map();
        filtered.forEach((s) => {
            const k = s.category || "Other";
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(s);
        });
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [shortcuts, query]);

    const total = shortcuts?.length || 0;
    const assigned = (shortcuts || []).filter((s) => !!s.combo).length;

    return (
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6" data-testid="shortcut-admin-page">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900">
                        <Keyboard size={22} weight="fill" className="text-indigo-700" />
                        Keyboard Shortcuts
                    </h1>
                    <p className="mt-1 text-sm text-slate-500">
                        {assigned} of {total} actions bound. Click the <b>✎</b> / <b>+</b> next to any row to open the visual keyboard, pick a key, and save. Applies to every user across every tenant immediately. {IS_MAC && " macOS displays ⌘ / ⌥ but the underlying binding is Ctrl / Alt."}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={resetDefaults}
                    disabled={busy}
                    data-testid="shortcut-reset-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                    <ArrowCounterClockwise size={14} weight="bold" /> Reset defaults
                </button>
            </div>

            <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
                <MagnifyingGlass size={14} className="text-slate-400" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search actions or shortcuts…"
                    data-testid="shortcut-search-input"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
            </div>

            {grouped.length === 0 && (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                    No matching shortcuts. Try a different search.
                </div>
            )}

            {grouped.map(([cat, rows]) => (
                <section key={cat} className="mb-6 overflow-visible rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                        {cat} · {rows.length} action{rows.length === 1 ? "" : "s"}
                    </div>
                    <table className="w-full text-sm" data-testid={`shortcuts-table-${cat.toLowerCase()}`}>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r) => (
                                <tr key={r.action_id} className="hover:bg-slate-50" data-testid={`shortcut-row-${r.action_id}`}>
                                    <td className="w-1/2 px-4 py-2.5 font-medium text-slate-800">{r.label}</td>
                                    <td className="px-4 py-2.5">
                                        {r.combo ? (
                                            <kbd data-testid={`shortcut-combo-${r.action_id}`} className="rounded-md bg-indigo-50 px-2 py-0.5 font-mono text-[11px] font-bold text-indigo-800 ring-1 ring-indigo-200">
                                                {displayCombo(r.combo)}
                                            </kbd>
                                        ) : (
                                            <span className="text-[11px] italic text-slate-400">not assigned</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <InlineShortcutAssigner
                                            action_id={r.action_id}
                                            label={r.label}
                                            target_route={r.target_route}
                                            category={r.category || "Menu"}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            ))}

            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] leading-relaxed text-slate-500">
                <b className="text-slate-700">Reserved combos</b> (cannot be assigned — browser depends on them):
                <span className="ml-1 font-mono">{BLOCKED.join(", ")}</span>.
                <div className="mt-1">Assigned combos apply everywhere: press the combo on any admin page to jump to that screen. A live cheat-sheet is also available via the keyboard button at the bottom-left of every page.</div>
            </div>
        </div>
    );
}
